import type { Map as MapboxMap, MapDataEvent } from 'mapbox-gl';

const MAPBOX_FQID_SEPARATOR = '\u001f';
const LINE_ELEVATION_REFERENCE = 'line-elevation-reference';
const LINE_Z_OFFSET = 'line-z-offset';

type MergedStyleLayer = {
  id: string;
  type: string;
  /** Import id (e.g. `basemap`); empty on parent-style layers. */
  scope?: string;
};

type FragmentLayoutApi = {
  getLayoutProperty: (layerId: string, name: string) => unknown;
  setLayoutProperty: (layerId: string, name: string, value: unknown) => unknown;
};

type MapStyleWithMergedLayers = {
  _mergedLayers: Record<string, MergedStyleLayer>;
  getFragmentStyle: (fragmentId?: string) => FragmentLayoutApi | undefined;
};

const isImportedLineLayer = (layer: MergedStyleLayer): boolean => {
  if (layer.type !== 'line') return false;
  if (!layer.scope) return false;
  if (!layer.id) return false;
  if (layer.id.includes(MAPBOX_FQID_SEPARATOR)) return false;
  return true;
};

const hasMergedLayers = (value: unknown): value is MapStyleWithMergedLayers => {
  if (!value || typeof value !== 'object') return false;
  if (!('_mergedLayers' in value) || !('getFragmentStyle' in value)) {
    return false;
  }
  return (
    typeof value._mergedLayers === 'object' &&
    value._mergedLayers !== null &&
    typeof value.getFragmentStyle === 'function'
  );
};

const getMapStyle = (map: MapboxMap): MapStyleWithMergedLayers | undefined => {
  if (!('style' in map)) return undefined;
  const style = Reflect.get(map, 'style');
  if (!hasMergedLayers(style)) return undefined;
  return style;
};

const isAlreadyDisabled = (
  fragment: FragmentLayoutApi,
  layerId: string,
): boolean => {
  try {
    const elevationReference = fragment.getLayoutProperty(
      layerId,
      LINE_ELEVATION_REFERENCE,
    );
    const zOffset = fragment.getLayoutProperty(layerId, LINE_Z_OFFSET);
    const zOffsetIsZero = zOffset === 0 || zOffset === undefined;
    return elevationReference === 'none' && zOffsetIsZero;
  } catch {
    return false;
  }
};

const disableLineProgressOnLayer = (
  fragment: FragmentLayoutApi,
  layerId: string,
): void => {
  if (isAlreadyDisabled(fragment, layerId)) return;
  // Ground/sea elevation and non-zero z-offset make Mapbox evaluate
  // line-progress; Standard vector tiles do not expose lineMetrics.
  fragment.setLayoutProperty(layerId, LINE_ELEVATION_REFERENCE, 'none');
  fragment.setLayoutProperty(layerId, LINE_Z_OFFSET, 0);
};

/**
 * Stops Mapbox Standard fence layers from evaluating `line-progress`.
 * Call as soon as imported line layers exist; returns false while the
 * fragment is not ready yet.
 */
export const disableImportedLineProgressEvaluation = (
  map: MapboxMap,
): boolean => {
  const style = getMapStyle(map);
  if (!style) return false;

  let foundImportedLine = false;
  let wroteAllLayers = true;
  for (const layer of Object.values(style._mergedLayers)) {
    if (!isImportedLineLayer(layer)) continue;
    const fragment = style.getFragmentStyle(layer.scope);
    if (!fragment) {
      wroteAllLayers = false;
      continue;
    }
    foundImportedLine = true;
    try {
      disableLineProgressOnLayer(fragment, layer.id);
    } catch {
      // Layer may not accept layout writes until the import finishes merging.
      wroteAllLayers = false;
    }
  }
  return foundImportedLine && wroteAllLayers;
};

/**
 * Applies {@link disableImportedLineProgressEvaluation} before the first
 * Standard tiles parse, and again if the style is reloaded.
 */
export const bindDisableImportedLineProgressEvaluation = (
  map: MapboxMap,
): void => {
  let applied = false;
  const apply = (): void => {
    if (applied) return;
    if (!disableImportedLineProgressEvaluation(map)) return;
    applied = true;
  };
  map.on('style.load', () => {
    applied = false;
    apply();
  });
  map.on('style.import.load', apply);
  map.on('data', (event: MapDataEvent) => {
    if (event.dataType !== 'style') return;
    apply();
  });
  apply();
};
